import React, { useCallback, useEffect, useRef, useState } from 'react';

type Point = { x: number; y: number };

type SignaturePadProps = {
  disabled?: boolean;
  onChange: (dataUrl: string | null) => void;
};

/** Logical drawing size (CSS pixels). Stored SVG uses this viewBox. */
const CANVAS_WIDTH = 180;
const CANVAS_HEIGHT = 60;
const MIN_POINT_DISTANCE = 1.5;
const STROKE_WIDTH = 2.25;

const roundCoord = (n: number) => Math.round(n * 10) / 10;

const pointsToPath = (points: Point[]): string => {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const p = points[0];
    return `M${p.x} ${p.y}l0.1 0`;
  }
  let d = `M${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i += 1) {
    d += `L${points[i].x} ${points[i].y}`;
  }
  return d;
};

const strokesToSvgDataUrl = (strokes: Point[][]): string => {
  const paths = strokes
    .filter((stroke) => stroke.length > 0)
    .map(
      (stroke) =>
        `<path d="${pointsToPath(stroke)}" fill="none" stroke="#2c2c2c" stroke-width="${STROKE_WIDTH}" stroke-linecap="round" stroke-linejoin="round"/>`,
    )
    .join('');
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}" width="${CANVAS_WIDTH}" height="${CANVAS_HEIGHT}">` +
    `<rect width="100%" height="100%" fill="#fff"/>` +
    paths +
    `</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const SignaturePad = ({ disabled = false, onChange }: SignaturePadProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const currentStrokeRef = useRef<Point[]>([]);
  const strokesRef = useRef<Point[][]>([]);
  const onChangeRef = useRef(onChange);
  const [hasStroke, setHasStroke] = useState(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(CANVAS_WIDTH * dpr);
    canvas.height = Math.floor(CANVAS_HEIGHT * dpr);
    canvas.style.width = '100%';
    canvas.style.height = 'auto';
    canvas.style.aspectRatio = `${CANVAS_WIDTH} / ${CANVAS_HEIGHT}`;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#2c2c2c';
    ctx.lineWidth = STROKE_WIDTH;

    const allStrokes = [...strokesRef.current];
    if (currentStrokeRef.current.length > 0) {
      allStrokes.push(currentStrokeRef.current);
    }

    for (const stroke of allStrokes) {
      if (stroke.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i += 1) {
        ctx.lineTo(stroke[i].x, stroke[i].y);
      }
      if (stroke.length === 1) {
        ctx.lineTo(stroke[0].x + 0.1, stroke[0].y);
      }
      ctx.stroke();
    }
  }, []);

  const clearCanvas = useCallback(() => {
    strokesRef.current = [];
    currentStrokeRef.current = [];
    drawingRef.current = false;
    setHasStroke(false);
    onChangeRef.current(null);
    paint();
  }, [paint]);

  useEffect(() => {
    clearCanvas();
  }, [clearCanvas]);

  const pointFromEvent = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_WIDTH / rect.width;
    const scaleY = CANVAS_HEIGHT / rect.height;
    return {
      x: roundCoord(Math.min(CANVAS_WIDTH, Math.max(0, (event.clientX - rect.left) * scaleX))),
      y: roundCoord(Math.min(CANVAS_HEIGHT, Math.max(0, (event.clientY - rect.top) * scaleY))),
    };
  };

  const exportSignature = () => {
    if (strokesRef.current.length === 0) {
      onChangeRef.current(null);
      return;
    }
    onChangeRef.current(strokesToSvgDataUrl(strokesRef.current));
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    event.preventDefault();
    canvas.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    currentStrokeRef.current = [pointFromEvent(event)];
    if (!hasStroke) setHasStroke(true);
    paint();
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || !drawingRef.current) return;
    event.preventDefault();

    const next = pointFromEvent(event);
    const stroke = currentStrokeRef.current;
    const last = stroke[stroke.length - 1];
    if (last) {
      const dx = next.x - last.x;
      const dy = next.y - last.y;
      if (dx * dx + dy * dy < MIN_POINT_DISTANCE * MIN_POINT_DISTANCE) return;
    }
    stroke.push(next);
    paint();
  };

  const endStroke = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    try {
      canvasRef.current?.releasePointerCapture(event.pointerId);
    } catch {
      // Pointer may already be released.
    }

    if (currentStrokeRef.current.length > 0) {
      strokesRef.current.push(currentStrokeRef.current);
      currentStrokeRef.current = [];
    }
    paint();
    exportSignature();
  };

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-2xl border-2 border-ink/10 bg-white">
        <canvas
          ref={canvasRef}
          className={`block w-full touch-none ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-crosshair'}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={(e) => {
            if (drawingRef.current) endStroke(e);
          }}
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="font-sans text-xs text-ink/55">
          {hasStroke ? 'Signature captured' : 'Draw your signature above'}
        </p>
        <button
          type="button"
          disabled={disabled || !hasStroke}
          onClick={clearCanvas}
          className="rounded-full px-3 py-1 font-sans text-xs font-semibold text-ink/70 hover:bg-ink/5 disabled:opacity-40"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
